import { useRef, useCallback, useState } from 'react';
import Webcam from 'react-webcam';
import { FaExclamationTriangle } from 'react-icons/fa';
import { prosesAbsensi } from '../../controller/Absensi_controller';
import LocationBadge from './LocationBadge';

const CameraCapture = ({
    onCapture,
    onError,
    ip_client,
    type = 'masuk',
    location = null,
    locationName = null,
    distanceLabel = null,
    /** Sementara: tampilkan lat,lng di badge (debug GPS) */
    coords = null,
    gpsDebug = null,
    /** Nonaktifkan ambil foto (mis. di luar radius) — kamera tetap tampil */
    captureDisabled = false,
    statusMessage = null,
    /** Mobile fullscreen: kamera isi area, tombol fixed di atas bottom nav */
    fullscreen = false,
}) => {
    const webcamRef = useRef(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isPressed, setIsPressed] = useState(false);

    const capture = useCallback(async () => {
        if (isLoading || captureDisabled) return;
        setErrorMessage(null);
        setIsLoading(true);

        const imageSrc = webcamRef.current?.getScreenshot();
        if (!imageSrc) {
            setErrorMessage('Gagal mengambil foto');
            setIsLoading(false);
            return;
        }

        try {
            const result = await prosesAbsensi(imageSrc, ip_client, type, location, locationName);
            onCapture(imageSrc, result);
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Gagal melakukan absensi';
            setErrorMessage(errorMsg);
            onError(new Error(errorMsg));
        } finally {
            setIsLoading(false);
        }
    }, [ip_client, isLoading, captureDisabled, type, location, locationName, onCapture, onError]);

    const showBlockedOverlay = captureDisabled && statusMessage;

    return (
        <div className={`w-full relative flex flex-col items-center ${fullscreen ? 'h-full' : 'h-full flex-1 min-h-0'}`}>
            <div className={`flex-1 flex items-center justify-center w-full min-h-0 ${fullscreen ? '' : 'px-3 md:px-8 md:pb-4'}`}>
                <div className={`relative w-full h-full min-h-0 ${fullscreen ? '' : 'max-h-full rounded-lg'}`}>
                    <div className={`absolute inset-0 overflow-hidden bg-black ${fullscreen ? '' : 'rounded-lg'}`}>
                        <Webcam
                            audio={false}
                            ref={webcamRef}
                            screenshotFormat="image/jpeg"
                            screenshotQuality={0.85}
                            mirrored={true}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                            }}
                            videoConstraints={{
                                width: { ideal: 1280 },
                                height: { ideal: 720 },
                                facingMode: 'user',
                            }}
                        />
                    </div>

                    {/* Gelapkan kamera + peringatan tengah */}
                    {showBlockedOverlay && (
                        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/55 px-6 pointer-events-none">
                            <div className="flex max-w-xs flex-col items-center gap-3 text-center">
                                <FaExclamationTriangle className="text-3xl text-white drop-shadow-md md:text-4xl" />
                                <p className="text-[15px] font-medium leading-snug text-white drop-shadow-md md:text-base">
                                    {statusMessage}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Di bawah tab overlay saat fullscreen mobile */}
                    <LocationBadge
                        name={locationName || 'Lokasi absensi'}
                        distance={distanceLabel}
                        coords={coords}
                        gpsDebug={gpsDebug}
                        top={fullscreen ? '4.25rem' : 8}
                    />
                </div>
            </div>

            {/* Error API (bukan status lokasi) — tetap di bawah */}
            {!showBlockedOverlay && errorMessage && (
                <p
                    className={`text-center text-red-200 text-sm font-medium px-4 mb-1 z-40 drop-shadow ${
                        fullscreen
                            ? 'absolute left-0 right-0 bottom-[calc(8.5rem+env(safe-area-inset-bottom,0px))] md:bottom-36'
                            : ''
                    }`}
                >
                    {errorMessage}
                </p>
            )}

            {/* Tombol Absensi */}
            <div
                className={`w-full flex justify-center flex-shrink-0 z-40
                    ${fullscreen
                        ? 'absolute left-0 right-0 bottom-[calc(6.25rem+env(safe-area-inset-bottom,0px))] md:bottom-10'
                        : 'fixed md:static left-0 right-0 bottom-[calc(4.75rem+env(safe-area-inset-bottom,0px))] md:bottom-auto md:mb-4'
                    }`}
            >
                <div className={`relative border-2 ${type === 'masuk' ? 'border-green-600' : 'border-[#602829]'} rounded-full p-2 overflow-hidden bg-white/50 ${captureDisabled ? 'opacity-45' : ''}`}>
                    <button
                        type="button"
                        className={`relative z-10 w-16 h-16 md:w-20 md:h-20 shadow-lg disabled:bg-gray-600 shadow-gray-500/50 ${type === 'masuk' ? 'bg-green-600 hover:bg-green-700' : 'bg-[#602829] hover:bg-[#7a3436]'
                            } text-white rounded-full p-4 text-lg font-semibold cursor-pointer transition-all duration-300 flex items-center justify-center overflow-hidden ${isPressed ? 'scale-95 translate-y-1' : 'scale-100 translate-y-0'
                            }`}
                        onClick={capture}
                        disabled={isLoading || captureDisabled}
                        onMouseDown={() => setIsPressed(true)}
                        onMouseUp={() => setIsPressed(false)}
                        onMouseLeave={() => setIsPressed(false)}
                        onTouchStart={() => setIsPressed(true)}
                        onTouchEnd={() => setIsPressed(false)}
                    >
                        <span className="relative z-10">
                            {isLoading ? (
                                <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <span className="text-lg font-light">Ambil</span>
                            )}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CameraCapture;
